import { RoleEnum } from '@acme/api';
import type { AuthSecretResponse } from '@acme/domains';
import type { Certificate } from 'crypto-pro';
import { useState } from 'react';
import { DigitalSignature, RoleSelection } from '.';
import { TwoFactorPasswordInput } from './twoFactorPasswordInput';

type AuthState =
  | { type: 'signature' }
  | { type: 'twoFactor'; cert: Certificate; secret: AuthSecretResponse }
  | { type: 'roleSelection'; cert: Certificate; secret: AuthSecretResponse };

interface DigitalSignatureLoginProps {
  onAuth: (
    cert: Certificate,
    secret: AuthSecretResponse,
    password?: string,
    role?: RoleEnum
  ) => void;
  loading?: boolean;
  error?: string | null;
  remainingCountOfPasswordAttempts?: number | null;
  supportsRoleSelection?: boolean;
}

export const DigitalSignatureLogin = ({
  onAuth,
  loading = false,
  error,
  remainingCountOfPasswordAttempts = null,
  supportsRoleSelection = false,
}: DigitalSignatureLoginProps) => {
  const [authState, setAuthState] = useState<AuthState>({ type: 'signature' });

  const handleSignatureSuccess = async (
    selectedCertificate: Certificate,
    secret: AuthSecretResponse
  ) => {
    if (secret.isTwoFactor) {
      setAuthState({
        type: 'twoFactor',
        cert: selectedCertificate,
        secret,
      });
    } else if (secret.isNewOrganization && supportsRoleSelection) {
      setAuthState({
        type: 'roleSelection',
        cert: selectedCertificate,
        secret,
      });
    } else {
      onAuth(selectedCertificate, secret);
    }
  };

  const handleTwoFactorSubmit = async (password: string) => {
    if (authState.type !== 'twoFactor') return;
    onAuth(authState.cert, authState.secret, password);
  };

  const handleRoleSelect = async (role: RoleEnum) => {
    if (authState.type !== 'roleSelection') return;
    onAuth(authState.cert, authState.secret, undefined, role);
  };

  return (
    <div className="font-inter flex flex-col gap-2 pb-10 text-center leading-[normal] items-center">
      {authState.type === 'twoFactor' ? (
        <TwoFactorPasswordInput
          onSubmit={handleTwoFactorSubmit}
          loading={loading}
          error={
            error && remainingCountOfPasswordAttempts !== null
              ? `Неверный пароль. Осталось попыток: ${remainingCountOfPasswordAttempts}`
              : error
          }
        />
      ) : authState.type === 'roleSelection' ? (
        <RoleSelection onSelectRole={handleRoleSelect} />
      ) : (
        <DigitalSignature callback={handleSignatureSuccess} />
      )}
    </div>
  );
};
